<template>
  <v-container>

    <dialog-scroll-lock width="500" :value="!!show" @input="show = null">
      <v-card>
        <v-btn class="float-right ma-4" icon @click="show = null"><v-icon>{{mdiClose}}</v-icon></v-btn>
        <v-card-title>{{show}}</v-card-title>
        <v-card-text v-if="show == 'Available fonts'">
          <div v-for="font in fonts" class="my-2" v-bind:style="{ 'font-family': font }">{{font}}</div>
        </v-card-text>
        <v-card-text v-else-if="show == 'User colors'">
          <div v-for="color in colors" class="my-2 d-flex align-center">
            <div style="width: 24px; height: 24px;" v-bind:style="{ 'background-color': color }"></div>
            <div class="ml-2">{{color}}</div>
          </div>
        </v-card-text>
      </v-card>
    </dialog-scroll-lock>

    <h1 class="mt-6 mb-3 text-h4 text-sm-h3">API Documentation</h1>

    <p>We provide an API to allow businesses / organizations to create and manage cards programmatically. This API works in conjunction with our other <router-link :to="{ name: 'business' }">organization tools</router-link>, like our analytics and custom branding. See also our documentation for building <router-link :to="{ name: 'video-template-docs' }">group video themes</router-link>. Please <a href="mailto:contact@ellacard.com">contact us</a> with any questions.</p>

    <faq-header anchor="table-of-contents" title="Contents" class="mt-12 mb-2 text-h5" />
    <ul>
      <li><a href="#getting-started">Getting started</a></li>
      <li><a href="#overview">Overview</a></li>
      <li><a href="#user-ids">User IDs</a></li>
      <li><a href="#design-ids">Design IDs</a></li>
      <li>
        <a href="#endpoints">Endpoints</a>
        <ul>
          <li><a href="#endpoint.auth">/auth</a></li>
          <li><a href="#endpoint.create-order">/create-order</a></li>
          <li><a href="#endpoint.get-order">/get-order</a></li>
          <li><a href="#endpoint.get-order-urls">/get-order-urls</a></li>
          <li><a href="#endpoint.update-order">/update-order</a></li>
          <li><a href="#endpoint.list-orders">/list-orders</a></li>
          <li><a href="#endpoint.cancel-order">/cancel-order</a></li>
        </ul>
      </li>
      <li><a href="#example">Example</a></li>
    </ul>

    <faq-header anchor="getting-started" title="Getting started" class="mt-12 mb-2 text-h5" />

    <p>First, get your API access key from the API tab in the <router-link :to="{ name: 'business' }">organization tools</router-link> page. You must be an organization administrator to access this page, and your organization must be enrolled in plan that supports API access. The tools there also let you delete and generate new access keys.</p>

    <p>The Ellacard API follows the <a target="_blank" href="https://op-dev.io">op-dev</a> pattern. You interact with the API using JSON remote procedure calls. There is a Python client available that makes it easier to interact with the API by automatically serializing / deserializing requests and managing re-authentication. See the <code>curl</code> example to better understand the raw mechanism / headers for making calls.</pre>

    <p>Please <a href="#example">see below</a> for a more comprehensive example of how to use the API.</p>

    <v-tabs class="mt-2 mb-4" v-model="tab" background-color="#0000" show-arrows>
      <v-tab>Python 3</v-tab>
      <v-tab>Curl</v-tab>
    </v-tabs>

    <v-tabs-items class="mb-6" v-model="tab" style="background-color: unset;">
      <v-tab-item>
        <div>First install the Python client:</div>
        <pre class="code mb-4">pip install https://pypi.op-dev.io/op-service-client/op-service-client-0.1.0.tar.gz</pre>
        <div>The following Python script connects to the API and lists your orders. Run it to test your connection:</div>
        <pre class="code">#!/usr/bin/env python3

# Set this variable first
API_ACCESS_KEY = …

import json
from op_service_client import OPServiceClient

client = OPServiceClient(
  url='https://api.ellacard.com',
  auth={ 'route': 'auth', 'params': { 'key': API_ACCESS_KEY } }
)

# The 'OPServiceClient' will automatically authenticate (and re-authenticate) as needed
output = client.call('list-orders')
print(json.dumps(output, indent=2))
</pre>
      </v-tab-item>
      <v-tab-item>
        <div>The following bash script shows how to use <code>curl</code> to connect to the API, authenticate, and list your orders. Run it to test your connection:</div>
        <pre class="code">#!/usr/bin/env bash

# Set this variable first
API_ACCESS_KEY=…

# First use the API access key to get a JWT. Note that the response to all API endpoints always comes back as JSON (even though it's just a string wrapped in quotes in this case), which is why we use 'jq' to parse it.
TOKEN=$(curl -s -X POST -H "Content-Type: application/json" https://api.ellacard.com/auth -d "{ \"key\": \"$API_ACCESS_KEY\" }" | jq -r .)

curl -s -X POST -H "Content-Type: application/json" -H "Authorization: Bearer $TOKEN" https://api.ellacard.com/list-orders | jq .
</pre>
      </v-tab-item>
    </v-tabs-items>

    <faq-header anchor="overview" title="Overview" class="mt-12 mb-2 text-h5" />

    <p>The Ellacard API lets you create, get, list, update, and cancel orders. All your orders will be tied to your organization (thus they'll automatically respect any custom branding you have enabled), and each order must be owned by an individual user in your organization. This allows you to use the Ellacard account tools to manually manage any individual order if necessary. Please <a href="mailto:contact@ellacard.com">contact us</a> to create an Ellacard organization account, or to add API access to your existing organization account.</p>

    <p>There are two primary ways users interact with Ellacard orders: via the order-sign and order-open views. Note that both of these views can be entirely embedded on your own website (see the "Embedding" tab in the <router-link :to="{ name: 'business' }">organization tools</router-link>). The order-sign view allows anyone to add content to the card without an account. Note the "allow_gift_cards" and "allow_replace_designs" flags in the create-order and update-order endpoints that give you some control over the features available in the order-sign interface. The owner of the order can also "lock" the order to prevent others from being able to add / edit content via the order-sign interface (this happens automatically when we deliver an order).</p>

    <p>You can set your orders to auto-send or not by specifying a "send_at" time. In the former case, each recipient you specify will receive a unique link that leads to the order-open view, which allows us to track which recipients have actually opened the order. We also generate a separete unique link that also leads to the order-open view, which you can distribute yourself.</p>

    <p>Note that you can update any of the order details at any time, unless the order has already been auto-sent.</p>

    <p>We provide a dashboard that summarizes all your organization's orders. It is accessible only to organization administrators in the orders tab in the <router-link :to="{ name: 'business' }">organization tools</router-link> page. You can also retrieve a list of your organization's orders via the list-orders API (using the appropriate access key).</p>

    <p>You can test your integration with the API using the sandbox access keys for free with all features available, but all associated orders will have a watermark. When placing an order in production, your organization's credits / discounts will be automatically applied.</p>

    <faq-header anchor="user-ids" title="User IDs" class="mt-12 mb-2 text-h5" />

    <p>Each order you create must be owned by a user who is a member of your organization. To find a user's ID, open the "Users" tab in the <router-link :to="{ name: 'business' }">organization tools</router-link> and click the info icon in the corresponding row.</p>

    <faq-header anchor="design-ids" title="Design IDs" class="mt-12 mb-2 text-h5" />

    <p>You can specify a design ID when creating an order. To find the ID for one of the designs in our <router-link :to="{ name: 'catalog' }">card catalog</router-link>, just select a design, then copy the URL suffix – https://ellacard.com/cards/<span class="red--text">xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx</span>. Note that you can also use your own custom designs, such as those created via the <router-link :to="{ name: 'img-to-design' }">create reusable card</router-link> tool – the resulting link has a similar suffix.</p>

    <faq-header anchor="endpoints" title="Endpoints" class="mt-12 mb-2 text-h5" />

    <faq-header anchor="endpoint.auth" title="/auth" class="mt-12 mb-2 text-h6" />
    <p>Uses your access key to get a temporary JWT that can be used to call other APIs. You can generate and delete access keys in the "API" tab in the <router-link :to="{ name: 'business' }">organization tools</router-link>.</p>
    <p>Note that the output JWT will have the "exp" property set, so you can check if you need to re-authenticate before making a service call (the Python 3 client does this automatically). You can use <a target="_blank" href="https://jwt.io/">this tool</a> to see the information we encode in the JWT.</p>
    <v-row class="pb-12">
      <v-col cols="12" md="6">
        <div class="overline short">Input</div>
<pre class="code">
{
  "key": non_empty_str
}
</pre>
      </v-col>
      <v-col cols="12" md="6">
        <div class="overline short">Output</div>
<pre class="code">
non_empty_str
</pre>
      </v-col>
    </v-row>

    <faq-header anchor="endpoint.create-order" title="/create-order" class="mt-12 mb-2 text-h6" />
    <p>Creates an order with the specified details and returns the order ID in addition to the order-sign and order-open links, which will respect any custom branding your organization has set up.</p>
    <v-row class="pb-12">
      <v-col cols="12" md="6">
        <div class="overline short">Input</div>
<pre class="code">
{
  "<a @click="popUp({ title: `user_id`, required: true, msg: `Must correspond to a member of your organization. You can get a user's ID by clicking on the info button in the 'User ID' column in the 'Users' tab in the organization tools.` })">user_id</a>": uuid_str,
  "<a @click="popUp({ title: `design_id`, msg: `If omitted or set to null, this order will be created using the blank design.` })">[design_id]</a>": nullable(uuid_str),
  "<a @click="popUp({ title: `from`, msg: `Who the card is from. Will be displayed to signers in the order-sign view and on the envelope in the order-open view. It will also be included in any delivery emails / texts sent by Ellacard.` })">[from]</a>": nullable(non_empty_str),
  "<a @click="popUp({ title: `recipients`, msg: `The recipients of the order. Only one of 'email' or 'text' should be set per recipient. Only relevant if the 'send_at' time is also set. Cannot exceed 500 recipients per order.<br><br>Note that only US phone numbers are currently supported, and should be formatted as '+1##########' or '(###) ###-####'.` })">[recipients]</a>": nullable([
    {
      "to": nullable(non_empty_str),
      "[email]": email_address,
      "[text]": us_phone_number
    },
    …
  ]),
  "<a @click="popUp({ title: `send_at`, msg: `The time at which Ellacard should deliver the order to the recipients. Omit or set to null to not send the order automatically. Any value in the past will cause the order to be delivered immediately. Should be a string formatted as '2020-01-01T00:00:00Z' or '2020-01-01T00:00:00+00:00' (must include timezone).` })">[send_at]</a>": nullable(iso_datetime_str),
  "<a @click="popUp({ title: `allow_gift_cards`, msg: `Set to true to allow users to add gift cards to this order.` })">[allow_gift_cards]</a>": bool,
  "<a @click="popUp({ title: `allow_replace_design`, msg: `Set to true to allow users to change the cover design of this order.` })">[allow_replace_design]</a>": bool,
  "<a @click="popUp({ title: `allow_universal_editing`, msg: `Set to true to allow any user to edit / delete any other user's blobs as opposed to just the owner of this order.` })">[allow_universal_editing]</a>": bool
}
</pre>
      </v-col>
      <v-col cols="12" md="6">
        <div class="overline short">Output</div>
<pre class="code">
{
  "order_id": uuid_str,
  "order": {
    "from": nullable(str),
    "recipients": nullable([
      {
        "to": nullable(non_empty_str),
        "<a @click="popUp({ title: `email`, msg: `Only one of 'email' or 'text' will be set.` })">[email]</a>": email_address,
        "<a @click="popUp({ title: `text`, msg: `Only one of 'email' or 'text' will be set.` })">[text]</a>": us_phone_number
      },
      …
    ]),
    "send_at": nullable(iso_datetime_str),
    "allow_gift_cards": bool,
    "allow_replace_design": bool,
    "allow_universal_editing": bool
  },
  "sign_url": non_empty_str,
  "open_url": non_empty_str
}
</pre>
      </v-col>
    </v-row>

    <faq-header anchor="endpoint.get-order" title="/get-order" class="mt-12 mb-2 text-h6" />
    <p>Retrieves an order that your organization has created via the API.</p>
    <v-row class="pb-12">
      <v-col cols="12" md="6">
        <div class="overline short">Input</div>
<pre class="code">
{
  "order_id": uuid_str
}
</pre>
      </v-col>
      <v-col cols="12" md="6">
        <div class="overline short">Output</div>
<pre class="code">
{
  "from": nullable(str),
  "recipients": nullable([
    {
      "to": nullable(non_empty_str),
      "<a @click="popUp({ title: `email`, msg: `Only one of 'email' or 'text' will be set.` })">[email]</a>": email_address,
      "<a @click="popUp({ title: `text`, msg: `Only one of 'email' or 'text' will be set.` })">[text]</a>": us_phone_number
    },
    …
  ]),
  "send_at": nullable(iso_datetime_str),
  "allow_gift_cards": bool,
  "allow_replace_design": bool,
  "allow_universal_editing": bool
}
</pre>
      </v-col>
    </v-row>

    <faq-header anchor="endpoint.get-order-urls" title="/get-order-urls" class="mt-12 mb-2 text-h6" />
    <p>Retrieves the order-sign and order-open URLs for an order that your organization has created via the API. Note that these URLs will respect any custom branding your organization has set up.</p>
    <v-row class="pb-12">
      <v-col cols="12" md="6">
        <div class="overline short">Input</div>
<pre class="code">
{
  "order_id": uuid_str
}
</pre>
      </v-col>
      <v-col cols="12" md="6">
        <div class="overline short">Output</div>
<pre class="code">
{
  "sign_url": non_empty_str,
  "open_url": non_empty_str
}
</pre>
      </v-col>
    </v-row>

    <faq-header anchor="endpoint.update-order" title="/update-order" class="mt-12 mb-2 text-h6" />
    <p>Updates an order that your organization has created via the API with the specified details. Note that this API can also be used to lock / unlock an order, which prevents other users from editing the card (the owner can always make edits).</p>
    <v-row class="pb-12">
      <v-col cols="12" md="6">
        <div class="overline short">Input</div>
<pre class="code">
{
  "order_id": uuid_str,
  "[order]": {
    "<a @click="popUp({ title: `user_id`, required: true, msg: `Must correspond to a member of your organization. You can get a user's ID by clicking on the info button in the 'User ID' column in the 'Users' tab in the organization tools.` })">user_id</a>": uuid_str,
    "<a @click="popUp({ title: `design_id`, msg: `If omitted or set to null, this order will be created using the blank design.` })">[design_id]</a>": nullable(uuid_str),
    "<a @click="popUp({ title: `from`, msg: `Who the card is from. Will be displayed to signers in the order-sign view and on the envelope in the order-open view. It will also be included in any delivery emails / texts sent by Ellacard.` })">[from]</a>": nullable(non_empty_str),
    "<a @click="popUp({ title: `recipients`, msg: `The recipients of the order. Only one of 'email' or 'text' should be set per recipient. Only relevant if the 'send_at' time is also set. Cannot exceed 500 recipients per order.<br><br>Note that only US phone numbers are currently supported, and should be formatted as '+1##########' or '(###) ###-####'.` })">[recipients]</a>": nullable([
      {
        "to": nullable(non_empty_str),
        "[email]": email_address,
        "[text]": us_phone_number
      },
      …
    ]),
    "<a @click="popUp({ title: `send_at`, msg: `The time at which Ellacard should deliver the order to the recipients. Omit or set to null to not send the order automatically. Any value in the past will cause the order to be delivered immediately. Should be a string formatted as '2020-01-01T00:00:00Z' or '2020-01-01T00:00:00+00:00' (must include timezone).` })">[send_at]</a>": nullable(iso_datetime_str),
    "<a @click="popUp({ title: `allow_gift_cards`, msg: `Set to true to allow users to add gift cards to this order.` })">[allow_gift_cards]</a>": bool,
    "<a @click="popUp({ title: `allow_replace_design`, msg: `Set to true to allow users to change the cover design of this order.` })">[allow_replace_design]</a>": bool,
    "<a @click="popUp({ title: `allow_universal_editing`, msg: `Set to true to allow any user to edit / delete any other user's blobs as opposed to just the owner of this order.` })">[allow_universal_editing]</a>": bool
  },
  "<a @click="popUp({ title: `lock`, msg: `Control whether or not other users (not the owner) can edit this order via the order-sign interface. Note that orders are automatically locked once auto-sent by Ellacard, but can be unlocked again if needed.` })">[lock]</a>": bool
}
</pre>
      </v-col>
      <v-col cols="12" md="6">
        <div class="overline short">Output</div>
<pre class="code">
{
  "from": nullable(str),
  "recipients": nullable([
    {
      "to": nullable(non_empty_str),
      "<a @click="popUp({ title: `email`, msg: `Only one of 'email' or 'text' will be set.` })">[email]</a>": email_address,
      "<a @click="popUp({ title: `text`, msg: `Only one of 'email' or 'text' will be set.` })">[text]</a>": us_phone_number
    },
    …
  ]),
  "send_at": nullable(iso_datetime_str),
  "allow_gift_cards": bool,
  "allow_replace_design": bool,
  "allow_universal_editing": bool
}
</pre>
      </v-col>
    </v-row>

    <faq-header anchor="endpoint.list-orders" title="/list-orders" class="mt-12 mb-2 text-h6" />
    <p>Lists all the orders that your organization has created via the API.</p>
    <v-row class="pb-12">
      <v-col cols="12" md="6">
        <div class="overline short">Input</div>
<pre class="code">
</pre>
      </v-col>
      <v-col cols="12" md="6">
        <div class="overline short">Output</div>
<pre class="code">
[
  {
    "order_id": uuid_str,
    "order": {
      "from": nullable(str),
      "recipients": nullable([
        {
          "to": nullable(non_empty_str),
          "<a @click="popUp({ title: `email`, msg: `Only one of 'email' or 'text' will be set.` })">[email]</a>": email_address,
          "<a @click="popUp({ title: `text`, msg: `Only one of 'email' or 'text' will be set.` })">[text]</a>": us_phone_number
        },
        …
      ]),
      "send_at": nullable(iso_datetime_str),
      "allow_gift_cards": bool,
      "allow_replace_design": bool,
      "allow_universal_editing": bool
    }
  },
  …
]
</pre>
      </v-col>
    </v-row>

    <faq-header anchor="endpoint.cancel-order" title="/cancel-order" class="mt-12 mb-2 text-h6" />
    <p>Cancels an order that your organization has created via the API. Orders may not be cancelled after they have already been auto-sent by Ellacard. Cancelled orders cannot be viewed in either the order-sign or order-open interfaces.</p>
    <v-row class="pb-12">
      <v-col cols="12" md="6">
        <div class="overline short">Input</div>
<pre class="code">
{
  "order_id": uuid_str
}
</pre>
      </v-col>
      <v-col cols="12" md="6">
        <div class="overline short">Output</div>
<pre class="code">
</pre>
      </v-col>
    </v-row>

    <faq-header anchor="example" title="Example" class="mt-12 mb-2 text-h5" />

    <p>This script will create an order, print the relevant links to the order-sign and order-open interfaces, then update the order to set it to send right away to the specified recipient.</p>

    <v-tabs class="mt-2 mb-4" v-model="tab" background-color="#0000" show-arrows>
      <v-tab>Python 3</v-tab>
      <v-tab>Curl</v-tab>
    </v-tabs>

    <v-tabs-items class="mb-6" v-model="tab" style="background-color: unset;">
      <v-tab-item>
        <pre class="code mb-4">pip install https://pypi.op-dev.io/op-service-client/op-service-client-0.1.0.tar.gz</pre>
        <pre class="code">#!/usr/bin/env python3

# Set these variables first
API_ACCESS_KEY = …
USER_ID = …
RECIPIENT_EMAIL_ADDRESS = …

import json
import time
from op_service_client import OPServiceClient

client = OPServiceClient(
  url='https://api.ellacard.com',
  auth={ 'route': 'auth', 'params': { 'key': API_ACCESS_KEY } }
)

# Create an order using the blank design. Note that we haven't set this order to auto-send.
create_order_output = client.call('create-order', { 'user_id': USER_ID })
print('Order structure after create-order:')
print(json.dumps(create_order_output['order'], indent=2))

order_id = create_order_output['order_id']

# You could share / display the "sign_url" to let others contribute to the order
print('Contribute to the order using this link:')
print(create_order_output['sign_url'])

# Or you could share the order-open URL to let others open the order
print('Open the order using this link:')
print(create_order_output['open_url'])

# Let's update the order and set it to send automatically
update_order_output = client.call(
  'update-order',
  {
    'order_id': order_id,
    'order': {
      'recipients': [ { 'to': None, 'email': RECIPIENT_EMAIL_ADDRESS } ],
      'send_at': '2021-01-01T00:00:00Z'
    }
  }
)
print('Order structure after update-order:')
print(json.dumps(update_order_output, indent=2))

# Now that the order has been sent, any further attempts to update the order data will fail. If you navigate to the 'sign_url' (as any user other than the one who created the order), you'll see that it has been locked and you are unable to contribute to it.

# Finaly, let's list all of our orders
list_orders_output = client.call('list-orders')
print('Output of list-orders:')
print(json.dumps(list_orders_output, indent=2))
</pre>
      </v-tab-item>
      <v-tab-item>
        <pre class="code">#!/usr/bin/env bash

# Set these variables first
API_ACCESS_KEY=…
USER_ID=…
RECIPIENT_EMAIL_ADDRESS=…

# First use the API access key to get a JWT
TOKEN=$(curl -s -X POST -H "Content-Type: application/json" https://api.ellacard.com/auth -d "{ \"key\": \"$API_ACCESS_KEY\" }" | jq -r .)

# Create an order using the blank design. Note that we haven't set this order to auto-send.
CREATE_ORDER_OUTPUT=$(curl -s -X POST -H "Content-Type: application/json" -H "Authorization: Bearer $TOKEN" https://api.ellacard.com/create-order -d "{ \"user_id\": \"$USER_ID\" }")
echo "Order structure after create-order:"
echo "$CREATE_ORDER_OUTPUT" | jq .order

ORDER_ID=$(echo "$CREATE_ORDER_OUTPUT" | jq -r .order_id)

# You could share / display the "sign_url" to let others contribute to the order
echo "Contribute to the order using this link:"
echo "$CREATE_ORDER_OUTPUT" | jq -r .sign_url

# Or you could share the order-open URL to let others open the order
echo "Open the order using this link:"
echo "$CREATE_ORDER_OUTPUT" | jq -r .open_url

# Let's update the order and set it to send automatically
UPDATE_ORDER_OUTPUT=$(curl -s -X POST -H "Content-Type: application/json" -H "Authorization: Bearer $TOKEN" https://api.ellacard.com/update-order -d "{ \"order_id\": \"$ORDER_ID\", \"order\": { \"recipients\": [ { \"to\": null, \"email\": \"$RECIPIENT_EMAIL_ADDRESS\" } ], \"send_at\": \"2021-01-01T00:00:00Z\" } }")
echo "Order structure after update-order:"
echo "$UPDATE_ORDER_OUTPUT" | jq .

# Now that the order has been sent, any further attempts to update the order data will fail. If you navigate to the 'sign_url' (as any user other than the one who created the order), you'll see that it has been locked and you are unable to contribute to it.

# Finaly, let's list all of our orders
LIST_ORDERS_OUTPUT=$(curl -s -X POST -H "Content-Type: application/json" -H "Authorization: Bearer $TOKEN" https://api.ellacard.com/list-orders)
echo "Output of list-orders:"
echo "$LIST_ORDERS_OUTPUT" | jq .
</pre>
      </v-tab-item>
    </v-tabs-items>

    <p class="mt-12">Please <a href="mailto:contact@ellacard.com">contact us</a> with any additional questions.</p>

  </v-container>
</template>


<style scoped>
pre.code {
  height: 100%;
  white-space: pre-wrap;
  font-size: 14px;
  background-color: #FBE5E1;
  color: #C0341D;
  padding: 0 0.4rem;
}
</style>


<script>
import FaqHeader from '@/components/FaqHeader.vue';
import { colors, fonts, frames } from '@/card-customization-options.js';
import { mdiClose } from '@mdi/js';

export default {
  name: 'api-docs',

  components: {
    FaqHeader
  },

  data() {
    return {
      tab: 0,
      show: null
    };
  },

  created() {
    Object.assign(this, { colors, fonts, frames: frames.filter(x => x).join(' | '), mdiClose });
  },

  methods: {
    popUp(params) {
      this.$confirm({ ...params, buttons: [ 'Ok' ], primary: 'Ok', persistent: false });
    }
  }
}
</script>
